import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/sidebar/Sidebar';
import movieService, {
  MovieProjectData,
  MovieProjectDetail,
  MovieScene,
  MovieVideoModel,
} from '../../services/api/movieService';
import ScriptStep, { SceneDraft } from '../../components/movie/ScriptStep';
import StoryboardStep from '../../components/movie/StoryboardStep';
import ClipStep from '../../components/movie/ClipStep';
import ConcatStep from '../../components/movie/ConcatStep';
import { ResponsiveAppShell, TaskyPageContainer } from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import { IMAGE_GENERATION_MODEL } from '../../constants/imageModel';
import {
  Clapperboard,
  Menu,
  ArrowLeft,
  Loader2,
  FileText,
  ImageIcon,
  Video,
  Combine,
  CheckCircle2,
} from 'lucide-react';

/**
 * ムービー制作エディタ。
 *
 * ① 台本・構成 → ② 絵コンテ → ③ クリップ → ④ 結合・書き出し
 * の4ステップをタブで自由に行き来できる（ウィザード式の強制遷移はしない）。
 *
 * - サーバー状態（data）はポーリングで更新される「真実」
 * - 台本編集用のドラフト（metaDraft / sceneDrafts）はローカル保持し、
 *   保存時にのみサーバーへ送る（ポーリングで編集中の内容が消えないように分離）
 */

type StepKey = 'script' | 'storyboard' | 'clip' | 'concat';

const POLLING_INTERVAL_MS = 5000;

const toSceneDrafts = (scenes: MovieScene[]): SceneDraft[] =>
  scenes.map((scene) => ({
    key: scene.id,
    id: scene.id,
    summary: scene.summary,
    duration_sec: scene.duration_sec,
    storyboard_prompt: scene.storyboard_prompt,
    clip_prompt: scene.clip_prompt,
  }));

const hasActiveWork = (data: MovieProjectData): boolean =>
  data.project.status === 'concatenating' ||
  data.scenes.some(
    (s) =>
      s.storyboard.status === 'pending' ||
      s.storyboard.status === 'processing' ||
      s.clip.status === 'pending' ||
      s.clip.status === 'processing'
  );

const MovieProjectEditorPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeStep, setActiveStep] = useState<StepKey>('script');
  const [data, setData] = useState<MovieProjectData | null>(null);
  const [metaDraft, setMetaDraft] = useState<MovieProjectDetail | null>(null);
  const [sceneDrafts, setSceneDrafts] = useState<SceneDraft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isSavingMeta, setIsSavingMeta] = useState(false);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [isSavingScenes, setIsSavingScenes] = useState(false);
  const [isConcatRequesting, setIsConcatRequesting] = useState(false);
  const [generatingSceneIds, setGeneratingSceneIds] = useState<Set<string>>(new Set());

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingRequestRef = useRef(false);

  /** サーバーから取得し、編集ドラフトは初回のみ初期化する */
  const applyData = useCallback((next: MovieProjectData, resetDrafts: boolean) => {
    setData(next);
    if (resetDrafts) {
      setMetaDraft(next.project);
      setSceneDrafts(toSceneDrafts(next.scenes));
    }
  }, []);

  const fetchProject = useCallback(
    async (resetDrafts: boolean) => {
      if (!projectId) return;
      try {
        const next = await movieService.getProject(projectId);
        applyData(next, resetDrafts);
        setError(null);
      } catch (e) {
        setError(movieService.extractErrorMessage(e));
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, applyData]
  );

  useEffect(() => {
    fetchProject(true);
  }, [fetchProject]);

  // アクティブなジョブがある間だけポーリング（ドラフトは触らない）
  useEffect(() => {
    if (!data || !hasActiveWork(data)) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    if (pollingRef.current) return;

    const interval = data.project.video_model.startsWith('fal-') ? 2000 : POLLING_INTERVAL_MS;
    pollingRef.current = setInterval(async () => {
      if (pollingRequestRef.current) return;
      pollingRequestRef.current = true;
      try {
        await fetchProject(false);
      } finally {
        pollingRequestRef.current = false;
      }
    }, interval);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [data, fetchProject]);

  const handleMetaChange = useCallback((patch: Partial<MovieProjectDetail>) => {
    setMetaDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const saveMeta = useCallback(async () => {
    if (!projectId || !metaDraft) return;
    setIsSavingMeta(true);
    setError(null);
    try {
      await movieService.updateProject(projectId, {
        title: metaDraft.title,
        concept: metaDraft.concept ?? '',
        style_description: metaDraft.style_description ?? '',
        aspect_ratio: metaDraft.aspect_ratio as '16:9' | '9:16' | '1:1',
        target_duration_sec: metaDraft.target_duration_sec,
        video_model: metaDraft.video_model as MovieVideoModel,
        image_model: IMAGE_GENERATION_MODEL,
      });
      setData((prev) => (prev ? { ...prev, project: { ...prev.project, ...metaDraft } } : prev));
    } catch (e) {
      setError(movieService.extractErrorMessage(e));
      throw e;
    } finally {
      setIsSavingMeta(false);
    }
  }, [projectId, metaDraft]);

  const handleGenerateScript = useCallback(async () => {
    if (!projectId) return;
    setIsGeneratingScript(true);
    setError(null);
    try {
      await saveMeta(); // コンセプトをサーバーに反映してから生成
      const next = await movieService.generateScript(projectId);
      applyData(next, true);
      showToast('台本の生成を開始しました');
    } catch (e) {
      setError(movieService.extractErrorMessage(e));
    } finally {
      setIsGeneratingScript(false);
    }
  }, [projectId, saveMeta, applyData, showToast]);

  const handleSaveScenes = useCallback(async () => {
    if (!projectId) return;
    setIsSavingScenes(true);
    setError(null);
    try {
      const next = await movieService.updateScenes(
        projectId,
        sceneDrafts.map((draft) => ({
          id: draft.id,
          summary: draft.summary ?? '',
          duration_sec: draft.duration_sec,
          storyboard_prompt: draft.storyboard_prompt ?? '',
          clip_prompt: draft.clip_prompt ?? '',
        }))
      );
      applyData(next, true);
    } catch (e) {
      setError(movieService.extractErrorMessage(e));
    } finally {
      setIsSavingScenes(false);
    }
  }, [projectId, sceneDrafts, applyData]);

  const markGenerating = useCallback((sceneId: string, on: boolean) => {
    setGeneratingSceneIds((prev) => {
      const next = new Set(prev);
      if (on) {
        next.add(sceneId);
      } else {
        next.delete(sceneId);
      }
      return next;
    });
  }, []);

  const handleGenerateStoryboard = useCallback(
    async (sceneId: string, options?: { suppressToast?: boolean }): Promise<boolean> => {
      if (!projectId) return false;
      markGenerating(sceneId, true);
      setError(null);
      try {
        await movieService.generateStoryboard(projectId, sceneId);
        await fetchProject(false);
        if (!options?.suppressToast) {
          showToast('絵コンテの生成を開始しました');
        }
        return true;
      } catch (e) {
        setError(movieService.extractErrorMessage(e));
        return false;
      } finally {
        markGenerating(sceneId, false);
      }
    },
    [projectId, fetchProject, markGenerating, showToast]
  );

  const handleGenerateClip = useCallback(
    async (sceneId: string, options?: { suppressToast?: boolean }): Promise<boolean> => {
      if (!projectId) return false;
      markGenerating(sceneId, true);
      setError(null);
      try {
        await movieService.generateClip(projectId, sceneId);
        await fetchProject(false);
        if (!options?.suppressToast) {
          showToast('動画クリップの生成を開始しました');
        }
        return true;
      } catch (e) {
        setError(movieService.extractErrorMessage(e));
        return false;
      } finally {
        markGenerating(sceneId, false);
      }
    },
    [projectId, fetchProject, markGenerating, showToast]
  );

  const handleGenerateAllStoryboards = useCallback(async () => {
    if (!data) return;
    const targets = data.scenes.filter(
      (s) => s.storyboard.status === 'none' || s.storyboard.status === 'failed'
    );
    // 直列に発行（同時実行上限への張り付きを避ける）。
    // 個別トーストはシーン数分スタックしてノイズになるため抑制し、最初の投入成功時に1回だけ表示する。
    let toastShown = false;
    for (const scene of targets) {
      // eslint-disable-next-line no-await-in-loop
      const succeeded = await handleGenerateStoryboard(scene.id, { suppressToast: true });
      if (succeeded && !toastShown) {
        showToast('絵コンテの生成を開始しました');
        toastShown = true;
      }
    }
  }, [data, handleGenerateStoryboard, showToast]);

  const handleGenerateAllClips = useCallback(async () => {
    if (!data) return;
    const targets = data.scenes.filter(
      (s) => s.storyboard.image && (s.clip.status === 'none' || s.clip.status === 'failed')
    );
    let toastShown = false;
    for (const scene of targets) {
      // eslint-disable-next-line no-await-in-loop
      const succeeded = await handleGenerateClip(scene.id, { suppressToast: true });
      if (succeeded && !toastShown) {
        showToast('動画クリップの生成を開始しました');
        toastShown = true;
      }
    }
  }, [data, handleGenerateClip, showToast]);

  const handleConcat = useCallback(async () => {
    if (!projectId) return;
    setIsConcatRequesting(true);
    setError(null);
    try {
      await movieService.concat(projectId);
      await fetchProject(false);
      showToast('動画の結合を開始しました');
    } catch (e) {
      setError(movieService.extractErrorMessage(e));
    } finally {
      setIsConcatRequesting(false);
    }
  }, [projectId, fetchProject, showToast]);

  const hasGeneratedAssets = useMemo(
    () => !!data && data.scenes.some((s) => s.storyboard.image || s.clip.video),
    [data]
  );

  const steps = useMemo(() => {
    const sceneCount = data?.scenes.length ?? 0;
    const storyboardDone = data?.scenes.filter((s) => s.storyboard.image).length ?? 0;
    const clipDone = data?.scenes.filter((s) => s.clip.video).length ?? 0;
    const finalDone = !!data?.project.final_video_url;

    return [
      {
        key: 'script' as StepKey,
        label: '① 台本・構成',
        icon: FileText,
        badge: sceneCount > 0 ? `${sceneCount}シーン` : null,
        done: sceneCount > 0,
      },
      {
        key: 'storyboard' as StepKey,
        label: '② 絵コンテ',
        icon: ImageIcon,
        badge: sceneCount > 0 ? `${storyboardDone}/${sceneCount}` : null,
        done: sceneCount > 0 && storyboardDone === sceneCount,
      },
      {
        key: 'clip' as StepKey,
        label: '③ クリップ',
        icon: Video,
        badge: sceneCount > 0 ? `${clipDone}/${sceneCount}` : null,
        done: sceneCount > 0 && clipDone === sceneCount,
      },
      {
        key: 'concat' as StepKey,
        label: '④ 結合・書き出し',
        icon: Combine,
        badge: null,
        done: finalDone,
      },
    ];
  }, [data]);

  if (isLoading) {
    return (
      <ResponsiveAppShell
        sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
        mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
        topbarTitle="ムービー制作"
      >
        <div className="flex min-h-[calc(100dvh-56px)] items-center justify-center bg-white text-gray-400 lg:h-screen">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          読み込み中...
        </div>
      </ResponsiveAppShell>
    );
  }

  if (!data || !metaDraft) {
    return (
      <ResponsiveAppShell
        sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
        mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
        topbarTitle="ムービー制作"
      >
        <div className="flex min-h-[calc(100dvh-56px)] flex-col items-center justify-center bg-white px-4 text-center text-gray-500 lg:h-screen">
          <p className="text-sm mb-3">{error ?? 'プロジェクトが見つかりません'}</p>
          <button
            onClick={() => navigate('/ads/movie')}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            一覧へ戻る
          </button>
        </div>
      </ResponsiveAppShell>
    );
  }

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle={metaDraft.title || 'ムービー制作'}
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <div className="hidden h-14 shrink-0 items-center border-b border-warm-border bg-white px-4 lg:flex">
          <div className="flex items-center space-x-3 min-w-0">
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="p-1 h-8 w-8 hover:bg-gray-100 rounded-md shrink-0"
                title="サイドバーを展開"
              >
                <Menu className="w-4 h-4" />
              </button>
            )}
            <button
              data-tutorial="ads-movie-back"
              onClick={() => navigate('/ads/movie')}
              className="p-1 h-8 w-8 hover:bg-gray-100 rounded-md shrink-0"
              title="一覧へ戻る"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <Clapperboard className="w-5 h-5 text-primary shrink-0" />
            <input
              type="text"
              value={metaDraft.title}
              onChange={(e) => handleMetaChange({ title: e.target.value })}
              onBlur={() => {
                if (metaDraft.title.trim() && metaDraft.title !== data.project.title) {
                  saveMeta().catch(() => undefined);
                }
              }}
              maxLength={100}
              className="text-lg font-semibold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-primary rounded px-1 min-w-0 flex-1"
            />
          </div>
        </div>

        {/* ステップタブ */}
        <div data-tutorial="ads-movie-steps" className="shrink-0 overflow-x-auto border-b border-warm-border bg-white px-4">
          <div className="mx-auto flex w-max max-w-6xl gap-1 lg:w-auto">
            {steps.map((step) => (
              <button
                key={step.key}
                onClick={() => setActiveStep(step.key)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm border-b-2 transition-colors ${
                  activeStep === step.key
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <step.icon className="w-4 h-4" />
                {step.label}
                {step.badge && (
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                      step.done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {step.badge}
                  </span>
                )}
                {step.done && !step.badge && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
              </button>
            ))}
          </div>
        </div>

        {/* Main */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                {error}
              </div>
            )}

            {activeStep === 'script' && (
              <ScriptStep
                project={metaDraft}
                scenes={sceneDrafts}
                hasGeneratedAssets={hasGeneratedAssets}
                isSavingMeta={isSavingMeta}
                isGeneratingScript={isGeneratingScript}
                isSavingScenes={isSavingScenes}
                onMetaChange={handleMetaChange}
                onSaveMeta={() => saveMeta().catch(() => undefined)}
                onGenerateScript={handleGenerateScript}
                onScenesChange={setSceneDrafts}
                onSaveScenes={handleSaveScenes}
              />
            )}

            {activeStep === 'storyboard' && (
              <StoryboardStep
                scenes={data.scenes}
                aspectRatio={data.project.aspect_ratio}
                generatingSceneIds={generatingSceneIds}
                onGenerate={handleGenerateStoryboard}
                onGenerateAll={handleGenerateAllStoryboards}
              />
            )}

            {activeStep === 'clip' && (
              <ClipStep
                scenes={data.scenes}
                aspectRatio={data.project.aspect_ratio}
                videoModel={data.project.video_model}
                generatingSceneIds={generatingSceneIds}
                onGenerate={handleGenerateClip}
                onGenerateAll={handleGenerateAllClips}
              />
            )}

            {activeStep === 'concat' && (
              <ConcatStep
                project={data.project}
                scenes={data.scenes}
                isRequesting={isConcatRequesting}
                onConcat={handleConcat}
              />
            )}
          </TaskyPageContainer>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default MovieProjectEditorPage;
